import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import { BasicStateTypes, CreatePostData } from '../types/types';
import { HEADERS } from '../utils/config';
import { AppSnackbar } from '../hooks/appSnackbar';

const initialState: BasicStateTypes = {
  loading: false,
  data: null,
  error: null,
};

const buildUrl = (
  baseUrl: string,
  params: {
    id?: number | null;
    pageNo?: string | null;
    keyword?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    categories?: number[] | null;
    visa?: number | null;
    perPage?: number;
    location?: string | null;
  }
): string => {
  const url = new URL(`${baseUrl}/posts${params.id ? `/${params.id}` : ''}`);

  const { pageNo, keyword, startDate, endDate, categories, visa, perPage, location } = params;

  if (pageNo) url.searchParams.append('page', pageNo);
  if (startDate && endDate) {
    url.searchParams.append('startDate', startDate);
    url.searchParams.append('endDate', endDate);
  }
  if (keyword) url.searchParams.append('title', keyword);
  if (location) url.searchParams.append('location', location);
  if (categories && categories.length > 0) {
    categories.forEach((categoryId) => url.searchParams.append('category[]', categoryId.toString()));
  }
  if (visa !== null && visa !== undefined) url.searchParams.append('visaType', visa.toString());
  if (perPage) url.searchParams.append('perPage', perPage.toString());

  return url.toString();
};

interface PostCrudParams {
  method: string;
  data: CreatePostData | null;
  id?: number | null;
  baseUrl: string;
  perPage?: number;
}

export const postCrud = createAsyncThunk(
  'post/postCrud',
  async (
    { method, data, id, baseUrl, perPage = 10 }: PostCrudParams,
    { rejectWithValue }
  ) => {
    const searchParams = new URLSearchParams(window.location.search);
    const pageNo = searchParams.get('page');
    const visaString = searchParams.get('visa');
    const visa = visaString ? parseInt(visaString, 10) : null;
    const keyword = searchParams.get('keyword');
    const location = searchParams.get('location');
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    const categories = searchParams.getAll('category').map(Number);
    
    try {
      const url = buildUrl(baseUrl, { id, pageNo, keyword, startDate, endDate, categories, visa, perPage, location });
      const config: AxiosRequestConfig = {
        method,
        url,
        headers: await HEADERS(),
        data,
      };
      const response = await axios(config);
      if (method === 'POST') {
        AppSnackbar({ type: 'success', message: 'Successfully created' });
      }
      return response.data;
    } catch (error: any) {
      console.log(error)
      if (error.code === 'ERR_BAD_REQUEST') {
        AppSnackbar({ type: 'error', message: 'Network error!' });
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const postSlice = createSlice({
  name: 'post',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(postCrud.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postCrud.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(postCrud.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default postSlice.reducer;
